// eslint-disable-next-line no-unused-vars
import React from 'react';
import { string, func, bool, number, array } from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withTheme } from '@emotion/react';
import omit from 'lodash/omit';

import withRouter from 'AppSrc/hocs/withRouter';
import withCart from 'AppSrc/hocs/withCart';
import withUser from 'AppSrc/hocs/withUser';
import withAnalytics from 'AppSrc/hocs/withAnalytics';
import ProductPropType from 'Types/product';
import { USER_TYPE } from 'Constants';
import ProductOrderItemComponent from 'Components/Products/ProductOrderItem';
import { ProductCartItem } from 'Containers/Products/ProductCartItem';
import parseExtraTax from 'Utils/parseExtraTax';

@withRouter
@withCart
@withUser
@withTranslation()
@withTheme
@withAnalytics
export default class ProductOrderItem extends ProductCartItem {
  constructor(props) {
    super(props);
    this.handleOnPriceChange = this.handleOnPriceChange.bind(this);
    this.handleOnChangeQuantity = this.handleOnChangeQuantity.bind(this);
    this.handleOnCancelChanges = this.handleOnCancelChanges.bind(this);
    this.handleOnDelete = this.handleOnDelete.bind(this);

    this.state = {
      ...this.state,
      unreservableProductsState: [],
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.ds !== nextProps.ds
      || this.props.actionsDisabled !== nextProps.actionsDisabled
      || this.props.editable !== nextProps.editable
      || this.props.orderId !== nextProps.orderId
      || this.props.shouldPromptChanges !== nextProps.shouldPromptChanges
      || this.props.canDelete !== nextProps.canDelete
      || this.props.selectedDSCountry !== nextProps.selectedDSCountry
      || this.props.dsCurrency !== nextProps.dsCurrency
      || this.props.defaultCurrency !== nextProps.defaultCurrency
      || this.props.unreservableProducts !== nextProps.unreservableProducts
      || this.props.unavailableProductIds !== nextProps.unavailableProductIds
      || super.shouldComponentUpdate(nextProps, nextState)
    );
  }

  /**
   * Delete rsvp product
   * @returns {Promise<void>}
   */
  handleOnDelete() {
    const { onRemove, orderId, analytics } = this.props;
    const { product } = this.state;
    analytics.track(`${ orderId ? 'reservation' : 'cart' }_product_deleted_clicked`, {
      order_id: this.props.orderId,
      product_id: product?.productId,
      product_sku: product?.code,
    });

    onRemove(product);
  }

  /**
   * Save changes made to product
   * @returns {Promise<void>}
   */
  async handleOnCancelChanges() {
    const { onRemove, onCancelRemove, onUpdate, analytics } = this.props;
    const { product } = this.state;

    analytics.track('reservation_cancel_changes', { order_id: this.props.orderId });

    if (product.new) {
      onRemove(product);
      return;
    }
    if (product.deleted) {
      const newProduct = {
        ...omit(product, ['deleted']),
      };
      onCancelRemove(newProduct);
      this.setState(prevState => ({
        ...prevState,
        product: newProduct
      }));
      return;
    }
    if (product.updated) {
      // eslint-disable-next-line no-unused-vars
      const { updated, originalQuantity: originalQty, ...restProduct } = product;

      const newProduct = {
        ...restProduct,
        quantity: originalQty,
      };

      if (newProduct.promoItems && Object.keys(newProduct.promoItems).length > 0) {
        newProduct.promoItems = Object.fromEntries(
          Object.entries(newProduct.promoItems).map(([promoKey, promoItems]) => [
            promoKey,
            promoItems.map(({ originalQuantity, ...newPromoItem }) => ({
              ...newPromoItem,
              quantity: originalQuantity,
            })),
          ])
        );
      }

      this.setState({ loading: true });

      const updatedProduct = omit(newProduct, ['originalQuantity', 'maxQuantityAvailable']);

      await onUpdate(updatedProduct);

      this.setState({
        loading: false,
        product: updatedProduct,
      });
    }
  }

  /**
   * Change product quantity
   * @param quantity
   */
  async handleOnChangeQuantity(quantity) {
    const { product } = this.state;
    const { onUpdate, shouldPromptChanges, orderId, analytics } = this.props;
    const newQty = quantity;
    const newProduct = {
      ...product,
      quantity: newQty
    };
    delete newProduct.maxQuantityAvailable;
    if (Object.entries(newProduct.promoItems || {}).length) {
      const step = (newQty - product.quantity) / product.minQuantity;
      newProduct.promoItems = Object
        .entries(newProduct.promoItems)
        .reduce((acc, [promoKey, promoItems]) => {
          acc[promoKey] = promoItems.map(promoItem => ({
            ...promoItem,
            quantity: promoItem.quantity + (step * promoItem.minQuantity),
            originalQuantity: 'originalQuantity' in promoItem
              ? promoItem.originalQuantity
              : promoItem.quantity
          }));
          return acc;
        }, {});
    }
    if ('originalQuantity' in product && newQty === product.originalQuantity) {
      delete newProduct.originalQuantity;
      delete newProduct.updated;
      this.setState({ loading: true });
      await onUpdate({ ...newProduct, quantity: newQty });
      this.setState(prevState => ({
        ...prevState,
        loading: false,
        product: newProduct
      }));
    } else if (newQty !== product.quantity) {
      if (!newProduct.new && !newProduct.updated && shouldPromptChanges) {
        newProduct.updated = true;
        newProduct.originalQuantity = product.quantity;
      }
      this.setState({ loading: true });
      try {
        await onUpdate(newProduct);
        this.setState(prevState => ({
          ...prevState,
          loading: false,
          product: newProduct
        }));
        analytics.track('quantity_changed', {
          order_id: orderId,
          new_quantity: quantity,
          previous_quantity: product.quantity,
          product_id: product?.id,
          product_sku: product?.code,
        });
      } catch (err) {
        if (err?.info?.status === 'error' && err?.info?.errors && err?.info?.errors[0].code === 'order.items.reservation_stock.invalid_reservation_stock') {
          this.setState({ unreservableProductsState: [product.code] });
        }
        this.setState(prevState => ({
          ...prevState,
          loading: false,
          product: {
            ...newProduct,
            maxQuantityAvailable: Math.min(product.stockValue + (product.originalQuantity || product.quantity), 100)
          }
        }));
      }
    }
  }

  handleOnPriceChange(product, price) {
    const { onChangePrice } = this.props;

    const decimalsToShow = 2;

    const newProduct = {
      ...product,
      promoUpdated: false,
      cashOnDeliveryValue: parseExtraTax(price, decimalsToShow)
    };

    if (newProduct.isPromo) {
      const stateProduct = this.state.product;
      stateProduct.promoUpdated = true;
      newProduct.addedCOD = true;
      stateProduct.promoItems[product.promotion.promotionId] = stateProduct.promoItems[product.promotion.promotionId]
        .reduce((acc, prod) => {
          if (prod.productId === product.productId) {
            acc.push(newProduct);
          } else {
            acc.push(prod);
          }
          return acc;
        }, []);
      onChangePrice(stateProduct);
    } else {
      onChangePrice(newProduct);
    }
    this.setState({ shouldUpdate: true });
  }

  render() {
    const { product, error, loading, unreservableProductsState } = this.state;
    const {
      ds,
      editable,
      actionsDisabled,
      canDelete,
      orderId,
      selectedDSCountry,
      dsCurrency,
      defaultCurrency,
      userContext: { type },
      expandRowProgrammatically,
      unreservableProducts,
      unavailableProductIds,
    } = this.props;

    return (
      <ProductOrderItemComponent
        orderId={ orderId }
        error={ error }
        dropShipment={ ds }
        editable={ editable }
        canDelete={ canDelete }
        product={ product }
        loading={ loading }
        dsCurrency={ dsCurrency }
        actionsDisabled={ actionsDisabled }
        selectedDSCountry={ selectedDSCountry }
        onCancelChanges={ this.handleOnCancelChanges }
        onDelete={ this.handleOnDelete }
        onChangeQuantity={ this.handleOnChangeQuantity }
        onChangePrice={ this.handleOnPriceChange }
        defaultCurrency={ defaultCurrency }
        isExternalReseller={ type === USER_TYPE.EXTERNAL_RESELLER }
        expandRowProgrammatically={ expandRowProgrammatically }
        unreservableProducts={ [...(unreservableProducts || []), ...unreservableProductsState] }
        unavailableProductIds={ unavailableProductIds }
      />
    );
  }
}

ProductOrderItem.displayName = 'ProductOrderItem';

ProductOrderItem.propTypes = {
  onChangePrice: func,
  actionsDisabled: bool,
  shouldPromptChanges: bool,
  orderId: string,
  onSave: func,
  onUpdate: func,
  onRemove: func,
  product: ProductPropType,
  canDelete: bool,
  editable: bool,
  ds: bool,
  selectedDSCountry: number,
  dsCurrency: string,
  defaultCurrency: string,
  expandRowProgrammatically: func,
  unreservableProducts: array,
  unavailableProductIds: array,
};
